import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { DEFAULT_LOCALE, LOCALES, messagesFor, type Locale, type Messages } from '@tms/i18n';
import { setApiLanguage } from '../api/client';

/**
 * Локаль кабинета (FR-557…FR-560).
 *
 * Тот же пакет ресурсов, что у приложения курьера. Это не экономия: подписи статусов
 * обязаны совпадать дословно (разд. 2.3.1), а два набора строк расходятся в первый же
 * месяц — и одно и то же состояние на карте и в телефоне называется по-разному.
 */

interface LocaleValue {
  readonly locale: Locale;
  readonly messages: Messages;
  readonly setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleValue | null>(null);

const STORAGE_KEY = 'tms.locale';

export function LocaleProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale());

  // Ошибки API и названия справочников приходят на том же языке, что и интерфейс
  // (FR-560): иначе половина экрана переключается, а сообщения об отказах — нет.
  useEffect(() => {
    setApiLanguage(locale);
    document.documentElement.lang = locale;
  }, [locale]);

  const value = useMemo<LocaleValue>(
    () => ({
      locale,
      messages: messagesFor(locale),
      setLocale: (next: Locale) => {
        setLocaleState(next);

        try {
          window.localStorage.setItem(STORAGE_KEY, next);
        } catch {
          // Приватный режим и заблокированное хранилище — не повод ломать интерфейс:
          // язык просто не переживёт перезагрузку вкладки.
        }
      },
    }),
    [locale],
  );

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleValue {
  const value = useContext(LocaleContext);

  if (value === null) {
    throw new Error('LocaleProvider is missing');
  }

  return value;
}

export function useMessages(): Messages {
  return useLocale().messages;
}

function readStoredLocale(): Locale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);

    if (stored !== null && (LOCALES as readonly string[]).includes(stored)) {
      return stored as Locale;
    }
  } catch {
    // см. выше
  }

  return DEFAULT_LOCALE;
}
